import type { Client, Server } from "stoat.js";
import { ulid } from "ulid";
import { writeStatusWithRetry } from "../../common/lib/statusWrite";

export type ServerTemplateId =
  | "custom"
  | "gaming"
  | "friends"
  | "study"
  | "club"
  | "team";
export type TemplateRoom = {
  key: string;
  name: string;
  type: "Text" | "Voice";
};
export type ServerTemplate = {
  id: ServerTemplateId;
  groups: { key: string; name: string; rooms: TemplateRoom[] }[];
};
const text = (key: string, name = key): TemplateRoom => ({
  key,
  name,
  type: "Text",
});
const voice = (key: string, name: string): TemplateRoom => ({
  key,
  name,
  type: "Voice",
});
export const serverTemplates: readonly ServerTemplate[] = [
  { id: "custom", groups: [] },
  {
    id: "gaming",
    groups: [
      {
        key: "chat",
        name: "Community",
        rooms: [
          text("general", "General"),
          text("announcements"),
          text("clips"),
        ],
      },
      {
        key: "voice",
        name: "Gaming rooms",
        rooms: [voice("lobby", "Lobby"), voice("squad", "Squad")],
      },
    ],
  },
  {
    id: "friends",
    groups: [
      {
        key: "chat",
        name: "Friends",
        rooms: [text("general", "General"), text("photos"), text("plans")],
      },
      { key: "voice", name: "Hang out", rooms: [voice("hangout", "Hangout")] },
    ],
  },
  {
    id: "study",
    groups: [
      {
        key: "study",
        name: "Study together",
        rooms: [
          text("general", "General"),
          text("resources"),
          text("questions"),
        ],
      },
      {
        key: "voice",
        name: "Study rooms",
        rooms: [voice("study-room", "Study Room")],
      },
    ],
  },
  {
    id: "club",
    groups: [
      {
        key: "club",
        name: "Club",
        rooms: [
          text("general", "General"),
          text("announcements"),
          text("events"),
        ],
      },
      {
        key: "voice",
        name: "Meet up",
        rooms: [voice("club-room", "Club Room")],
      },
    ],
  },
  {
    id: "team",
    groups: [
      {
        key: "team",
        name: "Team",
        rooms: [text("general", "General"), text("updates"), text("planning")],
      },
      {
        key: "voice",
        name: "Meetings",
        rooms: [voice("meeting-room", "Meeting Room")],
      },
    ],
  },
];
export const getServerTemplate = (id: ServerTemplateId = "custom") =>
  serverTemplates.find((template) => template.id === id) ?? serverTemplates[0];

export type TemplateDraft = {
  server?: Server;
  rooms: Record<string, string>;
  categories: Record<string, string>;
};
export const newTemplateDraft = (): TemplateDraft => ({
  rooms: {},
  categories: {},
});

/** Resume only the known created server; never recreate it after a room failure. */
export async function createServerFromTemplate(
  client: Client,
  name: string,
  template: ServerTemplate,
  draft: TemplateDraft,
  options: {
    signal: AbortSignal;
    progress?: (done: number, total: number) => void;
  },
) {
  const write = async <T>(operation: () => Promise<T>): Promise<T> => {
    let result!: T;
    await writeStatusWithRetry(
      async () => {
        result = await operation();
      },
      { signal: options.signal },
    );
    return result;
  };
  if (!draft.server)
    draft.server = await write(() =>
      client.servers.createServer({ name: name.trim() }),
    );
  const server = draft.server;
  if (!template.groups.length) return server;
  const rooms = template.groups.flatMap((group) => group.rooms);
  for (const [index, room] of rooms.entries()) {
    options.signal.throwIfAborted();
    if (!draft.rooms[room.key]) {
      const existing = server.channels.find(
        (channel) =>
          channel.name === room.name &&
          channel.type ===
            (room.type === "Voice" ? "VoiceChannel" : "TextChannel"),
      );
      // The default General channel is reused; no destructive cleanup is needed.
      const channel =
        existing ??
        (await write(() =>
          server.createChannel({ name: room.name, type: room.type }),
        ));
      draft.rooms[room.key] = channel.id;
    }
    options.progress?.(index + 1, rooms.length);
  }
  const categories = template.groups.map((group) => ({
    id: (draft.categories[group.key] ??= ulid()),
    title: group.name,
    channels: group.rooms.map((room) => draft.rooms[room.key]),
  }));
  await write(() => server.edit({ categories }));
  return server;
}
